import { supabase } from './supabase';

export interface ExtensionSettingsData {
  [extensionName: string]: {
    enabled?: boolean;
    [key: string]: any;
  };
}

/**
 * Get all extension settings for the current user
 */
export async function getMyExtensionSettings(): Promise<ExtensionSettingsData> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('You must be logged in to get settings');
  }
  
  const { data, error } = await supabase.rpc('get_my_extension_settings');
  
  if (error) {
    console.error('Error getting extension settings:', error);
    throw new Error('Failed to get extension settings');
  }
  
  return data || {};
}

/**
 * Update settings for a specific extension
 */
export async function updateExtensionSetting(extensionName: string, settings: any): Promise<ExtensionSettingsData> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('You must be logged in to save settings');
  }
  
  const { data, error } = await supabase.rpc('update_extension_setting', {
    extension_name: extensionName,
    settings: settings
  });
  
  if (error) {
    console.error('Error updating extension settings:', error);
    throw new Error('Failed to update extension settings');
  }
  
  return data || {};
}

/**
 * Replace all extension settings (use with caution)
 */
export async function updateAllExtensionSettings(settings: ExtensionSettingsData): Promise<ExtensionSettingsData> {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    throw new Error('You must be logged in to save settings');
  }
  
  const { data, error } = await supabase.rpc('update_my_extension_settings', {
    new_settings: settings
  });
  
  if (error) {
    console.error('Error updating all extension settings:', error);
    throw new Error('Failed to update all extension settings');
  }
  
  return data || {};
}

/**
 * Save extension settings for the current user (legacy compatibility)
 */
export async function saveExtensionSettings(extensionId: string, settings: any): Promise<void> {
  await updateExtensionSetting(extensionId, settings);
}

/**
 * Load extension settings for the current user (legacy compatibility)
 */
export async function loadExtensionSettings(): Promise<ExtensionSettingsData | null> {
  try {
    const settings = await getMyExtensionSettings();
    return settings;
  } catch (error) {
    console.error('Error loading extension settings:', error);
    return null;
  }
}

/**
 * Save specific chatbot settings
 */
export async function saveChatbotSettings(
  personality: string,
  voiceId: string,
  voiceName: string,
  enabled: boolean = true
): Promise<void> {
  await updateExtensionSetting('chat-bot', {
    enabled,
    personality, // Legacy key for backwards compatibility
    'bot-personality': personality, // Correct field ID
    voice_id: voiceId,
    voice_name: voiceName
  });
}

/**
 * Update extension enabled state
 */
export async function updateExtensionState(extensionId: string, enabled: boolean): Promise<void> {
  const currentSettings = await getMyExtensionSettings();
  const extensionSettings = currentSettings[extensionId] || {};
  
  await updateExtensionSetting(extensionId, {
    ...extensionSettings,
    enabled
  });
}

/**
 * Get extension enabled state
 */
export async function getExtensionState(extensionId: string): Promise<boolean> {
  const settings = await getMyExtensionSettings();
  return settings[extensionId]?.enabled || false;
}

/**
 * Get settings for a specific extension
 */
export async function getExtensionSettings(extensionId: string): Promise<any> {
  const settings = await getMyExtensionSettings();
  return settings[extensionId] || {};
}